/**
 * @typedef {Object} UserInfo
 * @property {string} firstName
 * @property {string} middleInitial
 * @property {string} lastName
 * @property {string} dobMonth
 * @property {string} dobDay
 * @property {string} dobYear
 * @property {string} driverAge
 * @property {string} gender
 * @property {string} maritalStatus
 * @property {string} relationshipToPolicyholder
 * @property {string} occupation
 * @property {string} militaryMember
 * @property {string} minorCustody
 */

/**
 * @typedef {Object} DriverInfo
 * @property {string} licenseStatus
 * @property {string} licenseAge
 * @property {string} licenseState
 * @property {string} licenseNumber
 * @property {string} licenseCountry
 * @property {string} diffNameLicensed
 * @property {string} diffStateLicensed
 * @property {string} hasInsurance
 * @property {string} insuranceType
 * @property {string} maintainOwnInsurance
 * @property {string} prevName
 * @property {string} prevNameLicenseNumber
 * @property {string} prevState
 * @property {string} prevStateLicenseNumber
 * @property {string} ownInsurance
 * @property {string} dateOccurred
 */

/**
 * @typedef {Object} AddressInfo
 * @property {string} driverStreetName
 * @property {string} driverAptNumber
 * @property {string} driverCity
 * @property {string} driverZip
 * @property {string} resideInState
 * @property {string} sameHousehold
 */

/**
 * @typedef {Object} SchoolInfo
 * @property {string} enrolledInCollege
 * @property {string} school100Miles
 * @property {string} schoolName
 * @property {string} schoolCity
 * @property {string} schoolState
 */

/**
 * @typedef {Object} DisabilityInfo
 * @property {string} disorders
 * @property {string} physicalImpairments
 */

/**
 * @typedef {Object} ViolationInfo
 * @property {string} movingViolations
 * @property {string} seriousMovingViolations
 * @property {string} driverSuspended
 * @property {string} underInfluence
 * @property {string} convicted
 * @property {string} vehicleDamage
 * @property {string} impairment
 * @property {string} injury
 * @property {string} ftsInjury
 * @property {string} provideInfoFailure
 * @property {string} recklessDriving
 */

/**
 * @typedef {Object} Vehicle
 * @property {string} Year
 * @property {string} Make
 * @property {string} Model
 * @property {string} VIN
 */

/**
 * @typedef {Object} VehicleInfo
 * @property {string} takeVehiclesToNewAddress
 * @property {Vehicle[]} vehiclesAtNewAddress
 */

/**
 * @typedef {Object} InitialDriverHHMType
 * @property {string} componentTracker
 * @property {string} requestedEffDate
 * @property {string} editType
 * @property {UserInfo} userInfo
 * @property {DriverInfo} driverInfo
 * @property {AddressInfo} addressInfo
 * @property {SchoolInfo} schoolInfo
 * @property {DisabilityInfo} disabilityInfo
 * @property {ViolationInfo} violationInfo
 * @property {VehicleInfo} vehicleInfo
 */

/**
 * Creates an InitialDriverHHM object with the provided context.
 * 
 * @param {Object} context The context object
 * @param {Function} context.getTodaysDate A function to get today's date
 * @param {Object} context.userData User-specific data, including state information
 * @returns {InitialDriverHHMType} The initial driver information object
 */
export const InitialDriverHHM = (context) => ({
    componentTracker: '',
    requestedEffDate: '',
    editType: '',
    userInfo: {
        firstName: '',
        middleInitial: '',
        lastName: '',
        dobMonth: '',
        dobDay: '',
        dobYear: '',
        driverAge: '',
        gender: '',
        maritalStatus: '',
        relationshipToPolicyholder: '',
        occupation: '',
        militaryMember: '',
        minorCustody: '',
    },
    driverInfo: {
        licenseStatus: '',
        licenseAge: '',
        licenseState: context?.userData?.state,
        licenseNumber: '',
        licenseCountry: '',
        diffNameLicensed: '',
        diffStateLicensed: '',
        hasInsurance: '',
        insuranceType: '',
        maintainOwnInsurance: '',
        prevName: '',
        prevNameLicenseNumber: '',
        prevState: '',
        prevStateLicenseNumber: '',
        ownInsurance: '',
        dateOccurred:'',
        principalStatus:''
    },
    addressInfo: {
        driverStreetName: '',
        driverAptNumber: '',
        driverCity: '',
        driverZip: '',
        resideInState: '',
        sameHousehold: ''
    },
    schoolInfo: {
        enrolledInCollege: '',
        school100Miles: '',
        schoolName: '',
        schoolCity: '',
        schoolState: '',
    },
    disabilityInfo: {
        disorders: '',
        physicalImpairments: '',
    },
    vehicleInfo: {
        takeVehiclesToNewAddress: '',
        vehiclesAtNewAddress: []
    },
    violationInfo: {
        movingViolations: '',
        seriousMovingViolations: '',
        driverSuspended: '',
        underInfluence: '',
        convicted: '',
        vehicleDamage: '',
        impairment: '',
        injury: '',
        ftsInjury: '',
        provideInfoFailure: '',
        recklessDriving: '',
    },
})